import React, { Component, useEffect } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { dashboardLoaded, userNotLoggedIn } from "../../actions/dashboardActions";
import { DashboardAuthError, dashboardQueryOptions } from "../../shared/dashboard";
import { endSessionEffects } from "../../shared/session";
import { useDashboardRefetchInterval } from "./utils/hooks";

import DocumentTheme from "../../components/DocumentTheme";

import AgentDashboard from "./AgentDashboard";
import ClientDashboard from "./ClientDashboard";
import BrokerDashboard from "./BrokerDashboard/BrokerDashboard";
import PageNotFound from "../PageNotFound";

class DashboardSelectorComponent extends Component {
  render() {
    if (!this.props.showLogin && !this.props.dashboardData.cases) {
      return null;
    }

    if (this.props.showLogin === true) {
      return <Navigate to="/login" />;
    }

    if (this.props.dashboardData) {
      switch (this.props.dashboardData.session.subject_type) {
        case "client":
          return <ClientDashboard />;

        // DocumentTheme switches the token sheet to the estate agent values.
        case "estate_agent":
          return (
            <>
              <DocumentTheme name="estate-agent" />
              <AgentDashboard />
            </>
          );

        case "broker":
          return <BrokerDashboard />;

        default:
          return <PageNotFound />;
      }
    }
  }
}

const mapStateToProps = (state) => {
  return {
    dashboardData: state.dashboardState,
    showLogin: state.loginState.showLogin,
  };
};

const ConnectedDashboardSelector = connect(mapStateToProps)(DashboardSelectorComponent);

// Loads the dashboard through TanStack Query and copies each response into
// Redux, which every dashboard page still reads.
const DashboardSelector = () => {
  const showLogin = useSelector((state) => state.loginState.showLogin);
  const subjectType = useSelector((state) => state.dashboardState.session?.subject_type);
  const dispatch = useDispatch();
  const refetchInterval = useDashboardRefetchInterval();

  // refetchOnMount is off so that coming back to the dashboard reuses the
  // cached response, as it reused Redux before
  const { data, error } = useQuery({
    ...dashboardQueryOptions,
    enabled: !showLogin,
    refetchOnMount: false,
    refetchInterval,
    // An estate agent's hourly refresh runs in hidden tabs too
    refetchIntervalInBackground: subjectType === "estate_agent",
  });

  useEffect(() => {
    if (data) {
      dispatch(dashboardLoaded(data));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (!error) return;
    if (error instanceof DashboardAuthError) {
      // Not resetSession: clearing the cache here would start another fetch.
      // The next sign-in clears it.
      endSessionEffects();
      dispatch(userNotLoggedIn());
    } else if (data === undefined) {
      console.error("Failed to load dashboard data", error);
      dispatch(userNotLoggedIn());
    } else {
      // A failed refresh leaves the last response on screen
      console.error("Failed to refresh dashboard data", error);
    }
  }, [error, data, dispatch]);

  return <ConnectedDashboardSelector />;
};

export default DashboardSelector;
